import { App, Button, Card, Form, Input, Space, Typography } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { adminChangeOwnPassword } from "../api/auth";
import { clearSession } from "../auth/store";

type FormValues = {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

/**
 * Self-service password change. Available to every role — the server enforces
 * "prove you know the old password" before writing a new hash. On success we
 * wipe the session and bounce to /login so the user reauthenticates with the
 * new credential rather than continuing on a now-rotated token.
 */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      adminChangeOwnPassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: () => {
      message.success("密码已修改，请重新登录");
      clearSession();
      navigate("/login", { replace: true });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          修改密码
        </Typography.Title>
      </Space>
      <Card style={{ maxWidth: 480 }}>
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => mutation.mutate(values)}
        >
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: "请输入当前密码" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 12, message: "密码至少 12 位" },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmNewPassword"
            label="确认新密码"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "请再次输入新密码" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("两次输入的密码不一致"));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            提交
          </Button>
        </Form>
      </Card>
    </div>
  );
}
